import React, { useRef, useState, useEffect } from 'react';
import { Modal, Button, Badge } from 'react-bootstrap';
import '../../../assets/scss/mention.scss';
import { SearchIcon } from './Icons';
import Avatar from './Avatar';
import { cloneDeep, isEmpty } from 'lodash';
import { fullName } from '../utils';

// Import icons used in ProjectModal (you may need to add these to your Icons.js file)
import { CheckboxIcon, UncheckedboxIcon,  } from '../components/Icons';
import CloseIcon from 'mdi-react/CloseIcon';

export default function AssignToModal({
  show,
  onHide,
  usersList = [],
  selectedUsers = [],
  persistentSelectedUsers = [], // New prop for users that persist across modal sessions
  onAssign,
  title = 'Assign To Users',
}) {
  const [searchString, setSearchString] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [originalUsers, setOriginalUsers] = useState([]);
  const [checkedUsers, setCheckedUsers] = useState(new Set());
  const [showPersistentUsers, setShowPersistentUsers] = useState(true);

  useEffect(() => {
    if (usersList && Array.isArray(usersList) && usersList.length > 0) {
      const users = cloneDeep(usersList);
      setOriginalUsers(users);
      setFilteredUsers(users);
    } else {
      setOriginalUsers([]);
      setFilteredUsers([]);
    }
  }, [usersList]);

  useEffect(() => {
    console.log('rendering');
    // Initialize checked users from both selectedUsers and persistentSelectedUsers
    const sessionSelected = new Set(selectedUsers.map((user) => user.id || user._id));
    const persistentSelected = new Set(persistentSelectedUsers.map((user) => user.id || user._id));

    // Merge both sets
    const allSelected = new Set([...sessionSelected, ...persistentSelected]);
    setCheckedUsers(allSelected);
  }, []);

  const handleFilterByName = (arr, value) => {
    if (!value.trim()) return arr;

    return arr.filter((member) => {
      const name = fullName(member.name);
      const username = member.username || '';
      const email = member.email || '';

      return (
        name.toLowerCase().includes(value.toLowerCase()) ||
        username.toLowerCase().includes(value.toLowerCase()) ||
        email.toLowerCase().includes(value.toLowerCase())
      );
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const searchParams = e.target.value;
    searchUsers(searchParams);
  };

  const searchUsers = (searchParams) => {
    searchParams = searchParams.trim();
    setSearchString(searchParams);

    if (!searchParams) {
      setFilteredUsers(originalUsers);
    } else {
      const filtered = handleFilterByName(originalUsers, searchParams);
      setFilteredUsers(filtered);
    }
  };

  const handleUserCheck = (user, isChecked) => {
    const userId = user._id;
    const newCheckedUsers = new Set(checkedUsers);

    if (isChecked) {
      newCheckedUsers.add(userId);
    } else {
      newCheckedUsers.delete(userId);
    }

    setCheckedUsers(newCheckedUsers);
  };

  const handleCheckAll = () => {
    const newCheckedUsers = new Set(checkedUsers);
    filteredUsers.forEach((user) => {
      const userId = user.id || user._id;
      newCheckedUsers.add(userId);
    });
    setCheckedUsers(newCheckedUsers);
  };

  const handleClearAll = () => {
    const newCheckedUsers = new Set(checkedUsers);
    filteredUsers.forEach((user) => {
      const userId = user.id || user._id;
      newCheckedUsers.delete(userId);
    });
    setCheckedUsers(newCheckedUsers);
  };

  const handleRemovePersistentUser = (userToRemove) => {
    const userId = userToRemove.id || userToRemove._id;
    const newCheckedUsers = new Set(checkedUsers);
    newCheckedUsers.delete(userId);
    setCheckedUsers(newCheckedUsers);
  };

  const handleAssign = () => {
    console.log('new user assigned');
    console.log(checkedUsers);
    const assignedUsers = originalUsers.filter((user) => checkedUsers.has(user._id));
    console.log({ assignedUsers });
    if (onAssign) {
      console.log('submitting to mother');
      onAssign(assignedUsers);
    }
    onHide();
  };

  const handleClose = () => {
    setSearchString('');
    setFilteredUsers(originalUsers);
    // Don't reset checkedUsers on close - maintain persistent selections
    onHide();
  };

  // Check if all filtered users are selected
  const areAllFilteredUsersSelected = () => {
    if (!filteredUsers.length) return false;
    return filteredUsers.every((user) => checkedUsers.has(user.id || user._id));
  };

  // Check if any filtered users are selected
  const areAnyFilteredUsersSelected = () => {
    return filteredUsers.some((user) => checkedUsers.has(user.id || user._id));
  };

  // Get currently selected users for display
  const getCurrentlySelectedUsers = () => {
    return originalUsers.filter((user) => checkedUsers.has(user.id || user._id));
  };

  const renderUserList = () => {
    if (!filteredUsers?.length) {
      return (
        <div className='text-center text-muted py-4'>
          {searchString ? 'No users found matching your search.' : 'No users available.'}
        </div>
      );
    }

    return filteredUsers.map((user, index) => {
      const userId = user.id || user._id;
      const isChecked = checkedUsers.has(userId);
      const isPersistent = persistentSelectedUsers.some(
        (pUser) => (pUser.id || pUser._id) === userId
      );

      return (
        <div className='user' key={userId || index}>
          <div className='grid-item'>
            {isChecked ? (
              <span onClick={() => handleUserCheck(user, false)}>
                <CheckboxIcon />
              </span>
            ) : (
              <span onClick={() => handleUserCheck(user, true)}>
                <UncheckedboxIcon className='not-checked' />
              </span>
            )}
          </div>
          <div className='grid-item'>
            <Avatar
              width={48}
              height={48}
              user={user}
              zIndex={1}
              showLeader={false}
              displayBadge={false}
            />
          </div>
          <div className='grid-item d-flex flex-column justify-content-start'>
            <span className='name'>{fullName(user.name)}</span>
            <span className='email'>
              {user.username ? `@${user.username}` : ''}
              {/* {user.email && (
                <span className='text-muted d-block' style={{ fontSize: '0.85em' }}>
                  {user.email}
                </span>
              )} */}
              {/* {isPersistent && (
                <span className='badge bg-light p-1 rounded ml-2'>previously selected</span>
              )} */}
            </span>
          </div>
        </div>
      );
    });
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size='lg'
      centered
      dialogClassName='project-modal'
      backdropClassName='global-backdrop project-backdrop max-z-index-2'
      animation={false}
    >
      <Modal.Header>
        <Modal.Title id='title'>{title}</Modal.Title>
        <button id='close-btn' onClick={handleClose}>
          <CloseIcon />
        </button>
      </Modal.Header>
      <Modal.Body>
        <div className='search'>
          <div className='search-box'>
            <input
              placeholder='Search users by name, username, or email...'
              value={searchString}
              onChange={(e) => handleSearch(e)}
              autoComplete='off'
            />
            <SearchIcon className='search-icon' />
          </div>
        </div>

        <div className='user-wrapper'>
          <div className='action align-items-center'>
            <div className='grid-item'>
              {areAllFilteredUsersSelected() ? (
                <span onClick={() => handleClearAll()}>
                  <CheckboxIcon />
                </span>
              ) : (
                <span onClick={() => handleCheckAll()}>
                  <UncheckedboxIcon className='not-checked' />
                </span>
              )}
            </div>
            <div>
              <span>Select All</span>
              {checkedUsers.size > 0 && (
                <span className='text-muted ml-2'>({checkedUsers.size} selected)</span>
              )}
            </div>
          </div>

          <div className='user-list'>{renderUserList()}</div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div>
          <button className='btn btn-outline-primary mr-3' type='button' onClick={handleClose}>
            Cancel
          </button>
          <button className='btn btn-primary final-btn' onClick={handleAssign}>
            Assign Selected ({checkedUsers.size})
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
